import { nullishToUndefinedable } from '@cc-shopping/utils/valibot';
import * as v from 'valibot';
import { EntityPlacementCtaTypeEnum, MarketplaceStatusEnum, RoundupDriverGroupEnum } from './enums';

export const driverGroupSchema = v.fallback(
  v.pipe(
    v.string(),
    v.transform(val => {
      // The driver group is empty for the default group.
      if (val === '') {
        return RoundupDriverGroupEnum.Default;
      }
      return val.toUpperCase();
    }),
    v.enum(RoundupDriverGroupEnum),
  ),
  RoundupDriverGroupEnum.Default,
);

export const pageTaxonomySchema = v.object({
  nwVarName: v.string('pageTaxonomy.nwVarName is required'),
  value: nullishToUndefinedable(v.string('pageTaxonomy.value must be a string when provided')),
});

export const analyticsPropsSchema = v.object({
  hasLegacyImpression: v.boolean('analyticsProps.hasLegacyImpression is required'),
  institutionId: v.string('analyticsProps.institutionId is required'),
  legacyIssuer: v.string('analyticsProps.legacyIssuer is required'),
  legacySlug: nullishToUndefinedable(
    v.string('analyticsProps.legacySlug must be a string when provided'),
  ),
  legacyVertical: nullishToUndefinedable(
    v.string('analyticsProps.legacyVertical must be a string when provided'),
  ),
  offerId: v.string('analyticsProps.offerId is required'),
  productId: v.string('analyticsProps.productId is required'),
});

export const breadcrumbSchema = v.object({
  page: v.object(
    {
      title: v.string('breadcrumb.page.title is required'),
    },
    'breadcrumb.page is required',
  ),
});

const ctaSchema = v.object({
  link: v.string('cta.link is required'),
  type: v.enum(
    EntityPlacementCtaTypeEnum,
    'cta.type must be a valid EntityPlacementCtaTypeEnum value',
  ),
  text: v.pipe(
    v.string('cta.text is required'),
    v.minLength(1, 'cta.text must have at least 1 character'),
  ),
  subtext: nullishToUndefinedable(v.string('cta.subtext must be a string when provided')),
  showIcon: v.boolean('cta.showIcon is required'),
});

// EntityPlacementImageSchema
export const baseOfferImageSchema = v.object({
  width: v.number('baseOfferImage.width is required'),
  height: v.number('baseOfferImage.height is required'),
  source: v.pipe(
    v.string('baseOfferImage.source is required'),
    v.trim(),
    v.url('baseOfferImage.source must be a valid URL'),
  ),
  sourceMedium: v.pipe(
    v.string('baseOfferImage.sourceMedium is required'),
    v.trim(),
    v.url('baseOfferImage.sourceMedium must be a valid URL'),
  ),
  sourceLarge: v.pipe(
    v.string('baseOfferImage.sourceLarge is required'),
    v.trim(),
    v.url('baseOfferImage.sourceLarge must be a valid URL'),
  ),
  resizableImagePath: nullishToUndefinedable(
    v.string('baseOfferImage.resizableImagePath must be a string when provided'),
  ),
});

export const bestOfAwardsSchema = v.object({
  category: v.string('bestOfAwards.category is required'),
  year: v.number('bestOfAwards.year is required'),
});

export const driverSchema = v.object({
  label: v.string('driver.label is required'),
  value: nullishToUndefinedable(v.string('driver.value must be a string when provided')), //TODO: why is this a thing?
  description: nullishToUndefinedable(
    v.string('driver.description must be a string when provided'),
  ),
  labelTooltip: nullishToUndefinedable(
    v.string('driver.labelTooltip must be a string when provided'),
  ),
  valueTooltip: nullishToUndefinedable(
    v.string('driver.valueTooltip must be a string when provided'),
  ),
  valueTooltipHasHtml: v.nullish(
    v.boolean('driver.valueTooltipHasHtml must be a boolean when provided'),
    false,
  ),
});

const rewardsRatesSchema = v.object({
  rate: v.number('rewardsRates.rate is required'),
  description: v.string('rewardsRates.description is required'),
});

export const marketplaceEntitySchema = v.object({
  id: v.string('id is required in marketplaceEntity'),
  name: v.string('name is required in marketplaceEntity'),
  displayName: nullishToUndefinedable(
    v.string('displayName must be a string when provided in marketplaceEntity'),
  ),
  entityType: nullishToUndefinedable(
    v.string('entityType must be a string when provided in marketplaceEntity'),
  ),
  consBullets: v.array(
    v.string('consBullet must be a string'),
    'consBullets must be an array of strings',
  ),
  prosBullets: v.array(
    v.string('prosBullet must be a string'),
    'prosBullets must be an array of strings',
  ),
  annualFee: v.number('annualFee is required in marketplaceEntity'),
  aprMin: nullishToUndefinedable(
    v.number('aprMin must be a number when provided in marketplaceEntity'),
  ),
  aprMax: nullishToUndefinedable(
    v.number('aprMax must be a number when provided in marketplaceEntity'),
  ),
  applyLink: v.pipe(
    v.string('applyLink is required in marketplaceEntity'),
    v.trim(),
    v.url('applyLink must be a valid URL'),
  ),
  approvalLikelihood: nullishToUndefinedable(
    v.number('approvalLikelihood must be a number when provided in marketplaceEntity'),
  ),
  legacyId: v.number('legacyId is required in marketplaceEntity'),
  limitedCreditHistory: v.boolean('limitedCreditHistory is required in marketplaceEntity'),
  ongoingApr: nullishToUndefinedable(
    v.string('ongoingApr must be a string when provided in marketplaceEntity'),
  ),
  minFico: v.number('minFico is required in marketplaceEntity'),
  maxFico: v.number('maxFico is required in marketplaceEntity'),
  marketingBullets: v.array(
    v.string('marketingBullet must be a string'),
    'marketingBullets must be an array of strings',
  ),
  monetizable: v.picklist(
    ['yes_assumed', 'no', 'no_because_of_limit'],
    'monetizable must be one of: yes_assumed, no, no_because_of_limit',
  ),
  phoneNumber: nullishToUndefinedable(
    v.string('phoneNumber must be a string when provided in marketplaceEntity'),
  ),
  reviewLink: v.pipe(
    v.string('reviewLink is required in marketplaceEntity'),
    v.trim(),
    v.url('reviewLink must be a valid URL'),
  ),
  rewardRateNotes: nullishToUndefinedable(
    v.string('rewardRateNotes must be a string when provided in marketplaceEntity'),
  ),
  rewardType: v.pipe(
    v.nullable(v.string('rewardType must be a string when provided'), 'no rewards'),
    v.transform(val => val?.toLowerCase()),
    v.picklist(
      ['no rewards', 'points', 'cash', 'cash back', 'miles'],
      'rewardType must be one of: no rewards, points, cash, cash back, miles',
    ),
  ),
  showNameInComparisonTable: nullishToUndefinedable(
    v.boolean('showNameInComparisonTable must be a boolean when provided in marketplaceEntity'),
  ),
  starRatingTooltip: nullishToUndefinedable(
    v.string('starRatingTooltip must be a string when provided in marketplaceEntity'),
  ),
  slug: nullishToUndefinedable(
    v.string('slug must be a string when provided in marketplaceEntity'),
  ),
  signUpBonus: v.number('signUpBonus is required in marketplaceEntity'),
  signUpBonusDetails: v.string('signUpBonusDetails is required in marketplaceEntity'),
  signUpBonusDetailsAnnotation: nullishToUndefinedable(
    v.string('signUpBonusDetailsAnnotation must be a string when provided in marketplaceEntity'),
  ),
  signUpBonusType: v.pipe(
    v.nullable(
      v.picklist(
        ['NO REWARDS', 'POINTS', 'CASH', 'MILES'],
        'signUpBonusType must be one of: NO REWARDS, POINTS, CASH, MILES',
      ),
      'NO REWARDS',
    ),
    v.transform(val => val.toLowerCase()),
  ),
  starRating: v.number('starRating is required in marketplaceEntity'),
  status: v.pipe(
    v.enum(MarketplaceStatusEnum, 'status must be a valid MarketplaceStatusEnum value'),
    v.transform(val => val?.toLowerCase()),
  ),
  termsAndFeesLink: nullishToUndefinedable(
    v.string('termsAndFeesLink must be a string when provided in marketplaceEntity'),
  ),
  versionId: v.number('versionId is required in marketplaceEntity'),
  analyticsProps: analyticsPropsSchema,
  cta: ctaSchema,
  driverAnnualFee: v.object(
    {
      value: v.string('value is required in driverAnnualFee'),
    },
    'driverAnnualFee must be a valid object',
  ),
  driverBonus: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverBonus'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverBonus'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverBonus'),
        ),
        description: nullishToUndefinedable(
          v.string('description must be a string when provided in driverBonus'),
        ),
      },
      'driverBonus must be a valid object',
    ),
  ),
  driverForeignTransactionFee: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverForeignTransactionFee'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverForeignTransactionFee'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverForeignTransactionFee'),
        ),
        description: nullishToUndefinedable(
          v.string('description must be a string when provided in driverForeignTransactionFee'),
        ),
      },
      'driverForeignTransactionFee must be a valid object',
    ),
  ),
  driverIntroApr: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverIntroApr'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverIntroApr'),
        ),
      },
      'driverIntroApr must be a valid object',
    ),
  ),
  driverRewardRate: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverRewardRate'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverRewardRate'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverRewardRate'),
        ),
        description: nullishToUndefinedable(
          v.string('description must be a string when provided in driverRewardRate'),
        ),
      },
      'driverRewardRate must be a valid object',
    ),
  ),
  driverIntroOffer: v.object(
    {
      label: v.string('label is required in driverIntroOffer'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverIntroOffer'),
      ),
      valueTooltip: nullishToUndefinedable(
        v.string('valueTooltip must be a string when provided in driverIntroOffer'),
      ),
      description: nullishToUndefinedable(
        v.string('description must be a string when provided in driverIntroOffer'),
      ),
    },
    'driverIntroOffer must be a valid object',
  ),
  driverRegularApr: nullishToUndefinedable(
    v.object(
      {
        label: v.string('label is required in driverRegularApr'),
        value: nullishToUndefinedable(
          v.string('value must be a string when provided in driverRegularApr'),
        ),
        valueTooltip: nullishToUndefinedable(
          v.string('valueTooltip must be a string when provided in driverRegularApr'),
        ),
      },
      'driverRegularApr must be a valid object',
    ),
  ),
  driverBalanceTransferFeeDescription: v.object(
    {
      label: v.string('label is required in driverBalanceTransferFeeDescription'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverBalanceTransferFeeDescription'),
      ),
      valueTooltip: nullishToUndefinedable(
        v.string(
          'valueTooltip must be a string when provided in driverBalanceTransferFeeDescription',
        ),
      ),
      description: nullishToUndefinedable(
        v.string(
          'description must be a string when provided in driverBalanceTransferFeeDescription',
        ),
      ),
    },
    'driverBalanceTransferFeeDescription must be a valid object',
  ),
  driverMinimumDeposit: v.object(
    {
      label: v.string('label is required in driverMinimumDeposit'),
      value: nullishToUndefinedable(
        v.string('value must be a string when provided in driverMinimumDeposit'),
      ),
      description: nullishToUndefinedable(
        v.string('description must be a string when provided in driverMinimumDeposit'),
      ),
    },
    'driverMinimumDeposit must be a valid object',
  ),

  // add other drivers here
  annualFeeDetails: nullishToUndefinedable(
    v.string('annualFeeDetails must be a string when provided in marketplaceEntity'),
  ),
  image: baseOfferImageSchema,
  institution: v.object(
    {
      name: v.string('name is required in institution'),
      id: v.string('id is required in institution'),
    },
    'institution must be a valid object',
  ),
  product: v.object(
    {
      name: v.string('name is required in product'),
      id: v.string('id is required in product'),
    },
    'product must be a valid object',
  ),
  productType: v.string('productType is required in marketplaceEntity'),
  bestOfAwards: v.array(bestOfAwardsSchema, 'bestOfAwards must be an array of best of awards'),
  rewardsRates: v.array(rewardsRatesSchema, 'rewardsRates must be an array of reward rates'),
  eligibleForNerdWalletPlusBonus: nullishToUndefinedable(
    v.boolean(
      'eligibleForNerdWalletPlusBonus must be a boolean when provided in marketplaceEntity',
    ),
  ),
  recommendedCreditScoreRangeDetails: nullishToUndefinedable(
    v.object(
      {
        creditScoreRange: v.string(
          'creditScoreRange is required in recommendedCreditScoreRangeDetails',
        ),
        description: v.string('description is required in recommendedCreditScoreRangeDetails'),
      },
      'recommendedCreditScoreRangeDetails must be a valid object',
    ),
  ),
  processingNetwork: nullishToUndefinedable(
    v.string('processingNetwork must be a string when provided in marketplaceEntity'),
  ),
  introPurchases: nullishToUndefinedable(
    v.string('introPurchases must be a string when provided in marketplaceEntity'),
  ),
  introBalanceTransfers: nullishToUndefinedable(
    v.string('introBalanceTransfers must be a string when provided in marketplaceEntity'),
  ),
  balanceTransferIntroTime: nullishToUndefinedable(
    v.number('balanceTransferIntroTime must be a number when provided in marketplaceEntity'),
  ),
  billingCycleUnits: v.string('billingCycleUnits is required in marketplaceEntity'),
});
